import { CheckCircle2, KeyRound, X } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../auth/use-auth'
import { api } from '../lib/api-client'
import { errorMessageKey, isApiError } from '../lib/api-error'
import { API_PREFIX } from '../lib/endpoints'
import { Button } from './ui/button'
import { Input } from './ui/input'

export const changePasswordEndpoint = `${API_PREFIX}/auth/change-password`
/** The API's own floor — `auth.schemas.ts`. */
export const MIN_PASSWORD_LENGTH = 12

/**
 * "Changer mon mot de passe", for every signed-in user.
 *
 * The API closes every session of the account on success, the current one included, so
 * the dialog does not pretend otherwise: it says the password changed and signs the
 * user out, and they come back in with the new one.
 */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pending, setPending] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !done) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [done, onClose])

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== next
  const sameAsCurrent = next.length > 0 && next === current
  const ready =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && confirm === next && !sameAsCurrent

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready || pending) return
    setPending(true)
    setErrorKey(null)
    try {
      await api.post(changePasswordEndpoint, { currentPassword: current, newPassword: next })
      setDone(true)
    } catch (error) {
      setErrorKey(
        isApiError(error) && error.code === 'invalid_credentials'
          ? 'common:changePassword.wrongCurrent'
          : errorMessageKey(error),
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={() => (done ? undefined : onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        data-testid="change-password-dialog"
        className="w-full max-w-md space-y-4 rounded-lg border border-border bg-surface p-5 shadow-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id="change-password-title"
            className="flex items-center gap-2 text-base font-semibold text-foreground"
          >
            <KeyRound aria-hidden className="h-5 w-5" />
            {t('common:changePassword.title')}
          </h2>
          {done ? null : (
            <button
              type="button"
              aria-label={t('common:actions.close')}
              onClick={onClose}
              className="rounded-md p-1 text-foreground-muted hover:bg-surface-hover hover:text-foreground"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          )}
        </div>

        {done ? (
          <div className="space-y-4">
            <p className="flex gap-2 text-sm text-foreground" role="status">
              <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              {t('common:changePassword.done')}
            </p>
            <div className="flex justify-end">
              <Button onClick={() => void logout()}>
                {t('common:changePassword.signInAgain')}
              </Button>
            </div>
          </div>
        ) : (
          <form className="space-y-3" onSubmit={(event) => void submit(event)}>
            <Input
              label={t('common:changePassword.current')}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
            <Input
              label={t('common:changePassword.new')}
              type="password"
              autoComplete="new-password"
              value={next}
              hint={t('common:changePassword.hint', { min: MIN_PASSWORD_LENGTH })}
              error={
                tooShort
                  ? t('common:changePassword.tooShort', { min: MIN_PASSWORD_LENGTH })
                  : sameAsCurrent
                    ? t('common:changePassword.sameAsCurrent')
                    : null
              }
              onChange={(event) => setNext(event.target.value)}
            />
            <Input
              label={t('common:changePassword.confirm')}
              type="password"
              autoComplete="new-password"
              value={confirm}
              error={mismatch ? t('common:changePassword.mismatch') : null}
              onChange={(event) => setConfirm(event.target.value)}
            />
            <p className="text-xs text-foreground-muted">
              {t('common:changePassword.signOutNote')}
            </p>
            {errorKey === null ? null : (
              <p role="alert" className="text-sm font-medium text-danger">
                {t(errorKey)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={!ready || pending}>
                {t('common:changePassword.submit')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
